import React, {useEffect, useRef, useState} from 'react'
import {Box, Link, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {Picture} from '_uiComponents'
import {useAnalytics, useIsElementInViewport, usePageDesigner} from '_customHooks'
import {PAGE_DESIGNER_MODULES, createRedirectUrl, getStoryAspectRatio, resolveStoryImages, type PageContentStoriesItemComponent} from '_utils'

export interface StoryCardProps {
    story: PageContentStoriesItemComponent
    /** Spans the whole grid row; set by the section layout or by the story's own `fullWidth` flag. */
    isFullWidth?: boolean
    /** Skips lazy loading for above-the-fold cards. */
    isPriority?: boolean
    /** 1-based position of the card, sent to analytics. */
    position?: number
    /** Page Designer visual layout of the section, sent to analytics when available. */
    template?: string
    /** The card type string to use in analytics events. */
    analyticsCardType?: 'single' | 'double' | 'triple'
}

/**
 * @name StoryCard
 *
 * @component
 *
 * @description
 * Renders a single Page Designer `pwaStoriesItem`: the responsive Cloudinary image plus the
 * story title and description. The whole card becomes a link when the editor set one.
 *
 * @param {StoryCardProps} props - The properties object.
 * @param {PageContentStoriesItemComponent} props.story - The mapped story.
 * @param {boolean} [props.isFullWidth] - Whether the card spans the whole grid row.
 * @param {boolean} [props.isPriority] - Whether the image should be eagerly loaded.
 * @param {number} [props.position] - The 1-based position of the card.
 * @param {string} [props.template] - The Page Designer visual layout of the section.
 *
 * @returns {JSX.Element} The rendered `StoryCard` component.
 */
const StoryCard: React.FC<StoryCardProps> = ({story, isFullWidth = false, isPriority = false, position = 1, template, analyticsCardType}) => {
    const styles = useMultiStyleConfig('Stories', {})
    const cardRef = useRef<HTMLDivElement>(null)
    const {analyticsEventPromoView, analyticsEventPromoClick} = useAnalytics()
    const isInViewport = useIsElementInViewport(cardRef)
    const [alreadySentPromoView, setAlreadySentPromoView] = useState(false)
    const {globalImageTransformation, CloudinaryPageDesignerCNAME, linkObject} = usePageDesigner()
    const {desktop, mobile, alt} = resolveStoryImages(story, globalImageTransformation, CloudinaryPageDesignerCNAME, isFullWidth)
    const aspectRatio = getStoryAspectRatio(story, isFullWidth)

    const href = story.link ? createRedirectUrl(story.link, linkObject.hostlink, linkObject.locale, linkObject.site) : undefined
    const cardMediaStyles = story.imageZoomHover ? {...styles.cardMedia, ...styles.cardMediaZoom} : styles.cardMedia

    useEffect(() => {
        if (!alreadySentPromoView && isInViewport) {
            analyticsEventPromoView({
                id: story.id ?? 'null',
                position,
                creative: `${PAGE_DESIGNER_MODULES.STORIES_CARD}-${analyticsCardType}`,
                name: story.name ?? 'null',
            })
            setAlreadySentPromoView(true)
        }
    }, [isInViewport, alreadySentPromoView, template])

    const content = (
        <>
            {mobile && (
                <Box sx={cardMediaStyles} aspectRatio={aspectRatio}>
                    <Picture
                        alt={alt}
                        imagexs={mobile}
                        imagemd={desktop}
                        imagelg={desktop}
                        objectFit="cover"
                        hasLazy={!isPriority}
                        fetchpriority={isPriority ? 'high' : 'low'}
                    />
                </Box>
            )}
            {story.title && <Text sx={styles.cardTitle}>{story.title}</Text>}
            {story.description && <Text sx={styles.cardDescription}>{story.description}</Text>}
        </>
    )

    return (
        <Box sx={styles.card} gridColumn={isFullWidth ? '1 / -1' : undefined} data-full-width={isFullWidth} data-testid="story-card" ref={cardRef}>
            {href ? (
                <Link
                    href={href}
                    sx={styles.cardLink}
                    onClick={() =>
                        analyticsEventPromoClick({
                            id: story.id ?? 'null',
                            position: String(position),
                            creative: `${PAGE_DESIGNER_MODULES.STORIES_CARD}-${analyticsCardType}`,
                            promoText: story.title ?? '',
                            name: story.name ?? 'null',
                        })
                    }
                >
                    {content}
                </Link>
            ) : (
                content
            )}
        </Box>
    )
}

export default StoryCard
